/** User-facing copy for Add Company search / preview failures (ID24-3789, ID24-3869, ID24-4134). */

const SA_REGISTRATION_YEAR_PATTERN = /(\d{4})[./]\d{1,6}[./]\d{2}/;
const SA_REGISTRATION_NUMBER_PATTERN = /^\d{4}[./]\d{6}[./]\d{2}$/;

export const COMPANY_SEARCH_NOT_FOUND_CURRENT_YEAR_MESSAGE =
	"Eish, we couldn't find that company. Please try again. If it was registered recently, please allow up to 7 days or add it manually.";

export const COMPANY_SEARCH_NOT_FOUND_PREVIOUS_YEAR_MESSAGE =
	"Eish, we couldn't find that company. Please try again or add it manually.";

export const COMPANY_DETAILS_UNAVAILABLE_MESSAGE =
	"We couldn't load the company details right now. Please try again later.";

export const COMPANY_SEARCH_TEMPORARY_MESSAGE =
	"We couldn't search for companies right now. Please try again later.";

export const COMPANY_ADD_INVALID_MESSAGE =
	'The company registration number is invalid. Please check and try again.';

export const COMPANY_ADD_GENERIC_MESSAGE =
	'An error occurred while adding company. Please click add company to retry.';

export const COMPANY_ALREADY_ADDED_MESSAGE = 'This company has already been added.';

type ApiErrorBody = {
	message?: string | string[];
	errors?: Record<string, string[] | string>;
	error_code?: number;
	status?: number;
};

export type HttpLikeError = {
	status?: number;
	error?: string | ApiErrorBody | null;
};

/** Resolve a usable HTTP status from an Angular HttpErrorResponse-like shape. */
export function resolveHttpStatus(error: HttpLikeError | null | undefined): number | undefined {
	if (!error) {
		return undefined;
	}

	if (typeof error.status === 'number' && error.status > 0) {
		return error.status;
	}

	const body = error.error;
	if (body && typeof body === 'object') {
		if (typeof body.error_code === 'number' && body.error_code > 0) {
			return body.error_code;
		}
		if (typeof body.status === 'number' && body.status > 0) {
			return body.status;
		}
	}

	return undefined;
}

function firstStringValue(value: unknown): string | undefined {
	if (typeof value === 'string' && value.trim()) {
		return value;
	}

	if (Array.isArray(value) && typeof value[0] === 'string' && value[0].trim()) {
		return value[0];
	}

	return undefined;
}

function resolveMessage(error: HttpLikeError): string | undefined {
	const body = error.error;
	if (!body || typeof body !== 'object') {
		return undefined;
	}

	return firstStringValue(body.message);
}

function firstValidationError(error: HttpLikeError): string | undefined {
	const body = error.error;
	if (!body || typeof body !== 'object' || !body.errors) {
		return undefined;
	}

	const first = Object.values(body.errors)[0];
	return firstStringValue(first);
}

export function getFirstApiErrorMessage(error: HttpLikeError, fallback: string): string {
	return firstValidationError(error) ?? resolveMessage(error) ?? fallback;
}

/** Strip trailing full stops and convert CIPC slashes for DataConfirm path lookup. */
export function normalizeCompanySearchQuery(query?: string | null): string {
	if (!query) {
		return '';
	}

	const trimmed = query.trim();
	const withoutTrailingStops = trimmed.replace(/\.+$/, '');

	if (SA_REGISTRATION_NUMBER_PATTERN.test(withoutTrailingStops)) {
		return withoutTrailingStops.replace(/\//g, '.');
	}

	return trimmed;
}

/** Extract the registration year from a CIPC-style number (YYYY/NNNNNN/NN or dotted). */
export function parseRegistrationYear(query?: string | null): number | undefined {
	if (!query) {
		return undefined;
	}

	const match = query.trim().match(SA_REGISTRATION_YEAR_PATTERN);
	if (!match) {
		return undefined;
	}

	return Number(match[1]);
}

/** 404 copy: 7-day guidance only when the registration year is the current year or unknown. */
export function getCompanySearchNotFoundMessage(query?: string | null): string {
	const registrationYear = parseRegistrationYear(query);
	const currentYear = new Date().getFullYear();

	if (registrationYear !== undefined && registrationYear < currentYear) {
		return COMPANY_SEARCH_NOT_FOUND_PREVIOUS_YEAR_MESSAGE;
	}

	return COMPANY_SEARCH_NOT_FOUND_CURRENT_YEAR_MESSAGE;
}

/** Map company search HTTP failures to actionable UI copy. */
export function getCompanySearchErrorMessage(error: HttpLikeError, query?: string | null): string {
	const status = resolveHttpStatus(error);

	if (status === 404) {
		return getCompanySearchNotFoundMessage(query);
	}

	return COMPANY_SEARCH_TEMPORARY_MESSAGE;
}

/** Map company preview / company-data HTTP failures to actionable UI copy. */
export function getCompanyDetailsErrorMessage(error: HttpLikeError, query?: string | null): string {
	const status = resolveHttpStatus(error);

	if (status === 404) {
		return getCompanySearchNotFoundMessage(query);
	}

	if (status === 422) {
		return resolveMessage(error) ?? COMPANY_ADD_INVALID_MESSAGE;
	}

	return COMPANY_DETAILS_UNAVAILABLE_MESSAGE;
}

/** Map add-company HTTP failures to actionable UI copy. */
export function getCompanyAddErrorMessage(error: HttpLikeError, query?: string | null): string {
	const status = resolveHttpStatus(error);

	if (status === 404) {
		return getCompanySearchNotFoundMessage(query);
	}

	if (status === 422) {
		return firstValidationError(error) ?? resolveMessage(error) ?? COMPANY_ADD_INVALID_MESSAGE;
	}

	// Surface API messages for access-only / owned-company failures (e.g. ID24-3962).
	if (status === 400) {
		return resolveMessage(error) ?? COMPANY_ADD_GENERIC_MESSAGE;
	}

	return resolveMessage(error) ?? COMPANY_ADD_GENERIC_MESSAGE;
}
